import { ensurePackageBundled, type EnsurePackageBundledContext } from './ensurePackageBundled.js';
import { PerfMeasurementSyncUpload } from '../performance/markers.js';
import { getRemoteCachePromiseResultCounts } from '../utilities/getRemoteCachePromiseResultCounts.js';
import { createRemoteCacheClientOperationOptions } from '../utilities/createRemoteCacheClientOperationOptions.js';
import { createUploadTask } from '../utilities/createUploadTask.js';
import { isStaticRoute } from '@ms-cloudpack/common-types';
import path from 'path';
import { getBundleLocation } from '../utilities/getBundleLocation.js';
import { cp } from 'fs/promises';
import objectHash from 'object-hash';
import type { SyncResult } from '../types/SyncResult.js';
import { ensureRemoteCacheEnabled } from '../utilities/ensureRemoteCacheEnabled.js';
import { getRemoteCachePackageId } from '../utilities/getRemoteCachePackageId.js';
import type { LoginMethod } from '@ms-cloudpack/remote-cache';
import { createImportMap } from '@ms-cloudpack/import-map';
import { serializeContext } from '../utilities/serializeContext.js';
import { writeJson } from '@ms-cloudpack/json-utilities';

const templateAppServerUrl = 'template://app-server-url';
const templateBundleServerUrl = 'template://bundle-server-url';

// TODO: don't hardcode this
const targetEnvironment = 'browser';

/**
 * Bundle the current app and its dependencies, and upload them to the remote cache.
 */
export async function syncUpload(
  input: { login: LoginMethod },
  ctx: Omit<EnsurePackageBundledContext, 'bus' | 'watcher'>,
): Promise<SyncResult> {
  const { login } = input;
  const { session, telemetryClient, taskRunner, remoteCacheClient: client } = ctx;
  const { config } = session;
  const { appPath } = config;
  const { remoteCache: remoteCacheConfig } = config;
  const syncInternalPackages = config?.features?.syncInternalPackages ?? false;

  ensureRemoteCacheEnabled(config);

  if (!client) {
    throw new Error('Remote cache client is required to sync upload');
  }

  if (!remoteCacheConfig) {
    throw new Error('Remote cache configuration is required to sync upload');
  }

  // Glob pattern for the files to upload.
  const globMatches = ['**/*', '!**/*-input.json', '!**/*-output.json'];

  return await telemetryClient.tracer.startActiveSpan(PerfMeasurementSyncUpload, async (span) => {
    const packageJson = await ctx.packages.get(appPath);
    const { name, version, description } = packageJson;

    const { dependencies, result: bundleResult } = await ensurePackageBundled(
      { name, version, enqueueDependencies: true, shouldGetBundleInfo: false },
      ctx,
    );

    const bundledPackages = [bundleResult, ...(await dependencies)];
    const packagesToUpload = await Promise.all(
      bundledPackages
        .filter((p) => p.inputPath && (p.isExternal || syncInternalPackages))
        .map((p) =>
          createRemoteCacheClientOperationOptions(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- verified above that it's set
            { packagePath: p.inputPath!, targetEnvironment },
            ctx,
          ),
        ),
    );

    if (config.features?.enableCloudHosted) {
      const appBundleLocation = await getBundleLocation({ packagePath: appPath, targetEnvironment }, ctx);
      const appOutputPath = appBundleLocation.path;
      const destinationFolder = path.join(appOutputPath, '__cloudpack');
      const routesPath = path.join(destinationFolder, 'routes');
      for (const route of config.routes ?? []) {
        if (isStaticRoute(route)) {
          const staticPath = path.resolve(appPath, route.staticPath);
          const routeHash = objectHash(route);
          const folderPath = path.join(routesPath, routeHash);
          await cp(staticPath, path.join(folderPath, route.staticPath), { recursive: true });
        }
      }

      if (!session.urls.appServer) {
        // Using template URLs for the app and bundle servers, because they are not known at this point and the will be replaced with the actual URLs at runtime.
        session.urls.appServer = templateAppServerUrl;
        session.urls.bundleServer = templateBundleServerUrl;
      }
      session.importMap ??= await createImportMap({ targetEnvironment: 'browser' }, ctx);
      await serializeContext(destinationFolder, ctx);

      await writeJson(path.join(destinationFolder, 'package.json'), packageJson);

      const { createAppRegistryClient } = await import('@ms-cloudpack/remote-cache');
      const appRegistryClient = await createAppRegistryClient({
        context: ctx,
        options: { cachePath: session.cachePath, loginMethod: login },
      });

      await appRegistryClient.registerApp({
        name,
        // Using the current date and time as the version, because most apps are not versioned.
        version: new Date().toISOString().slice(0, 16).replace('T', ' '),
        resourceId: await getRemoteCachePackageId({ packagePath: appPath, targetEnvironment }, ctx),
        storageAccount: remoteCacheConfig.storageAccount,
        container: remoteCacheConfig.container,
        tenantId: remoteCacheConfig.tenantId,
        description,
        localBundleFolderName: appBundleLocation.folderName,
      });
    }

    const uploads = packagesToUpload.map((pkg) =>
      taskRunner.add(createUploadTask({ client, options: { ...pkg, globMatches } })),
    );

    // Do not reject if there is a failure. Failed uploads will be logged in the task reporter.
    const promiseResults = await Promise.allSettled(uploads);

    const result = getRemoteCachePromiseResultCounts(promiseResults);
    span.setAttributes(result);

    return result;
  });
}

// This function doesn't have an attached procedure because it's never called via the TRPC server.
